// ===========================================================================
// Entering a static dashboard from its card
//
// A static screen persists its filter selection per dashboard (localStorage,
// `<name>-static-filter-state:<key>`) so a refresh, or a trip out to a detail
// page and back, returns to the view the user had built. Opening the dashboard
// from its card is the one arrival that should NOT do that: clicking Live or
// History is an explicit "start here", and picking up a week-old selection there
// reads as the screen being broken — filters narrowed, tiles missing, a Reset
// chip already lit before the user has touched anything.
//
// The dynamic pages draw the same line in the same place: openPage() clears the
// time-cycle filter and a handful of session keys before it navigates.
//
// A flag rather than the caller clearing the state itself, because the storage
// key belongs to the screen — each one namespaces its own, and only it knows what
// is in there. The card only knows that a fresh visit is starting. The flag is
// consumed on arrival, so it governs that one load: everything the user changes
// afterwards persists and survives a refresh exactly as before.
//
// Its own file, small as it is, because a static screen has to import it: the
// registry in static-dashboards.ts imports the component classes, so anything a
// component imports back from there would be a cycle — and one that resolves as
// an undefined `component` in STATIC_DASHBOARDS, i.e. a blank dashboard rather
// than a build error.
// ===========================================================================
const ENTRY_FLAG_KEY = "static-dashboard-entry";

/** Record that a card click is opening this static dashboard. Called by
 *  dashboard.component's openStaticPage() just before it navigates. */
export function markStaticDashboardEntry(dashboardKey: any): void {
  try {
    sessionStorage.setItem(ENTRY_FLAG_KEY, String(dashboardKey ?? ""));
  } catch {
    /* storage unavailable (private mode / quota) — the screen then restores its
       saved selection, which is the pre-existing behaviour, not a broken one */
  }
}

/**
 * True exactly once per card click, for the dashboard that was clicked — the
 * screen's cue to open on its defaults and forget what it had saved.
 *
 * Keyed by dashboard so opening DPM-Static cannot reset DEM-Static's selection.
 * Not keyed by view: Live and History are two buttons on one card, so switching
 * between them is a card click like any other and starts clean.
 */
export function consumeStaticDashboardEntry(dashboardKey: any): boolean {
  try {
    const flagged = sessionStorage.getItem(ENTRY_FLAG_KEY);
    if (flagged === null || flagged !== String(dashboardKey ?? "")) return false;
    sessionStorage.removeItem(ENTRY_FLAG_KEY);
    return true;
  } catch {
    return false;
  }
}
